import { animated, Spring } from '@react-spring/web';
import { Col, Row } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Button, media, notification, Span } from 'ui';
import { announcementBannerHeight, errorActions, publishSteps } from 'utils/app-configs';
import moment from 'utils/cops-moment';
import { isObjectNullOrEmpty } from '../../../utils/helpers';
import useCollapseExpand from '../../../utils/hooks/useCollapseExpand';
import { boardIdTypes } from '../../gantt/config/view';
import WarningNotification from '../WarningNotification';

export const errorSummaryStyles = {
  marginLeft: '-20px',
  top: '161px',
  width: 'calc(100% + 40px) !important',
  position: 'sticky',
  zIndex: 120,
};

const StyledDiv = styled.div`
  padding: ${p => p.hasInfo || p.hasErrors || p.hasWarnings ? 20 : 0}px 0;
  border: ${p =>
    // eslint-disable-next-line
    p.isValidationError && `2px solid ${p.hasErrors ? '#CC0000' : p.hasWarnings ? '#ff9600' : p.theme.colors.theme}`
  };
  > :first-child {
    margin-bottom: ${p => p.hasInfo ? 20 : 0}px !important;
  }
`;

const Buttons = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 5px;
  justify-content: flex-end;
`;

const ErrorTitle = styled.h4`
  color: ${p => p.theme.colors.publishSteps.error};
  font-weight: 600;
  text-transform: uppercase;
`;

const WarningTitle = styled.h4`
  color: ${p => p.theme.colors.alerts[2]};
  font-weight: 600;
  text-transform: uppercase;
`;

const ErrorTitleCollapsed = styled(ErrorTitle)`
  color: ${p => p.theme.colors.publishSteps.statusses[p.validationStatus]} !important;
  font-size: 16px;
  margin-left: 150px;
`;

const InfoAction = styled.div`
  width: 70%;
`;

const InfoTitle = styled.h4`
  color: ${p => p.theme.colors.theme};
  font-weight: 600;
  text-transform: uppercase;
`;

const SmallExternalInfoMessage = styled.h4`
  color: ${p => p.theme.colors.theme};
  font-weight: 400;
  text-transform: uppercase;
  margin: 10px 0;
`;

const StyledCol = styled(Col)`
  border-right: ${p => p.bordered && `1px solid ${p.theme.colors.publishSteps.border}`};
  padding-left: ${p => p.paddingleft};
  padding-right: ${p => p.paddingright};
  @media (max-width: 770px) {
    padding-left: 15px;
  }
`;

const StyledUl = styled.ul`
  list-style: disc;
  padding-left: 15px;
`;

const StyledActionButton = styled(Button)`
  text-transform: uppercase;
  font-size: 12px;
  border-color: ${p => p.theme.colors.publishSteps.primary} !important;
  background-color: ${p => p.type || 'transparent'} !important;
  color: ${p => p.type || p.theme.colors.publishSteps.primary} !important;
  &:hover {
    border-color: ${p => p.theme.colors.publishSteps.primary} !important;
    background-color: ${p => p.type || 'transparent'} !important;
    color: ${p => p.type || p.theme.colors.publishSteps.primary} !important;
    opacity: 0.7;
  }
  .ant-btn-loading-icon{
    padding-right:10px;
  }
`;

// Is necessary because antd doesn't allow type'secondary' in a notification
const StyledConfirmButton = styled(Button)`
  margin-left: 10px;
  text-transform: uppercase;
  border-color: #6f7e95 !important;
  background-color: ${p => p.isprimary ? '#6f7e95' : 'transparent'} !important;
  color: ${p => p.isprimary ? 'white' : '#6f7e95'} !important;
  &:hover {
    border-color: #6f7e95 !important;
    background-color: ${p => p.isprimary ? '#6f7e95' : 'transparent'} !important;
    color: ${p => p.isprimary ? 'white' : '#6f7e95'} !important;
    opacity: 0.7;
  }
`;

const StyledInfoIcon = styled.i`
  margin-right: 10px;
`;

const StyledSpan = styled(Span)`
  font-size: 14px;
`;

const ButtonCol = styled(Col)`
  @media (min-width: 768px) {
    width: auto;
    margin-right: 10px;
  }
`;

const StyledMessage = styled.span`
  font-weight: normal;
  white-space: pre-wrap;
  font-size: 16px;
  margin-right: 20px;
`;

const StyledLi = styled.li`
  ${p => p.isGts ? 'margin-left: 60px;' : ''}
  white-space: pre-wrap;
  font-weight: normal;
  font-size: 16px;
`;

const StyledBoldMessage = styled.span`
  font-weight: bold;
`;

const DescriptionTitle = styled.p`
  line-height: 15px;
  font-weight: normal;
  font-size: 14px;
`;

const DescriptionSubTitle = styled.p`
  line-height: 15px;
  font-weight: lighter;
  font-size: 14px;
`;

const StyledRow = styled(Row)`
  margin-bottom: 15px;
  @media (min-width: 768px) {
    flex-flow: row nowrap;
  }
`;

const StyledContainer = styled(animated.div)`
  ${media.tablet`
    top: 55px;
    transform: unset !important;
  `};
  z-index: 110;
  background-color: rgb(245, 248, 250) !important;
  width: 100%;
  ${p => p.extraStyles ?
    p.extraStyles :
    ''
  }
`;

const Text = styled.p`
  ${p => p.marginTop && `margin-top: ${p.marginTop}px;`}
  white-space: pre-wrap;
`;

const ShowMore = styled.div`
  margin-left: 50px;
`;

const Arrow = styled.span`
  color: ${p => p.theme.colors.publishSteps.statusses[p.validationStatus]} !important;
  margin-left: 20px;
  font-size: 12px;
  text-decoration: underline;
  font-weight: 300;
  text-transform: none;
  &:hover {
    cursor: pointer;
  }
  i {
    color: ${p => p.theme.colors.publishSteps.statusses[p.validationStatus]} !important;
  }
`;

const ErrorSummary = ({
  discardLockRequest,
  onDataReloaded,
  deleteDraftRequest,
  unlockLockRequest,
  intl,
  isLoading,
  isStale = false,
  status,
  backupHistory,
  actions,
  isValidationError = false,
  timeoutInMinutes,
  isTimedOut = false,
  errors,
  info = [],
  warnings = [],
  menuVisible,
  verticalVersion = false,
  isPlanningBoard = false,
  hasPlanningItems = false,
  isFromOtherEntity = false,
  otherEntityType,
  renderShowMoreToggle,
  isExternalCreate = false,
  isExternalUpdate = false,
  extraStyles = null,
  isGtsBlocked = false,
  isGtsBlockedPlanning = false,
  isDeletingDraft = false,
  isStandAlone = false,
  actionOnly = false,
  isFromXML = false,
  resizeCb,
}) => {
  const [arrow, content, isCollapsed, setCollapsed] = useCollapseExpand(false);
  const { hasAnnouncementBanner } = useSelector(state => state.user);

  const handleConfirm = (id, url) => {
    discardLockRequest({
      id,
      url,
      reloadFn: onDataReloaded,
      isPlanningBoard,
      boardId: boardIdTypes.service,
    });
    notification.destroy('confirmWarning');
  };

  const handleButtonClick = (action) => {
    if (action.type === errorActions.update.type) {
      unlockLockRequest({
        id: action.id,
        url: action.url,
        reloadFn: onDataReloaded,
        isPlanningBoard,
        boardId: boardIdTypes.service,
      });
    } else if (action.type === errorActions.resetToDraft.type) {
      handleConfirm(action.id, action.url);
    } else if (action.type === errorActions.reset.type) {
      let time = null;
      if (action?.data?.timestamp) {
        time = moment(action.data?.timestamp).fromNow(true);
      }
      const btn =
        <Buttons>
          <StyledConfirmButton
            isprimary={0}
            onClick={() => notification.destroy('confirmWarning')}
            loading={isLoading}
            disabled={isLoading}
          >
            {intl.formatMessage({ id: 'publishSteps.CANCEL' })}
          </StyledConfirmButton>
          <StyledConfirmButton
            isprimary={1}
            onClick={() => handleConfirm(action.id, action.url)}
            loading={isLoading}
            disabled={isLoading}
          >
            {intl.formatMessage({ id: 'publishSteps.OK' })}
          </StyledConfirmButton>
        </Buttons>;
      notification.warning({
        message: intl.formatMessage({ id: 'publishSteps.RESET_CONFIRM_QUESTION' }),
        description: <>
          {time && intl.formatMessage({ id: 'publishSteps.RESET_CONFIRM_DESCRIPTION' }, { time })}
          {btn}
        </>,
        key: 'confirmWarning',
        className: 'extended-notification',
        duration: 0,
        placement: 'top',
      });
    } else {
      deleteDraftRequest({ url: action.url });
    }
  };

  const renderActionDescription = (action) => {
    let descriptionTitle = <FormattedMessage id='publishSteps.DESCRIPTION_NONE' />;
    if (action) {
      switch (action.type) {
        case errorActions.delete.type:
          descriptionTitle = <FormattedMessage id='publishSteps.DESCRIPTION_DELETE' />;
          break;
        case errorActions.update.type:
          descriptionTitle = !isStale ?
            <FormattedMessage id='publishSteps.DESCRIPTION_UPDATE' />
            :
            <p>
              <StyledBoldMessage>
                <FormattedMessage id='publishSteps.DESCRIPTION_STALE_TITLE' />
              </StyledBoldMessage>{' '}
              <FormattedMessage id='publishSteps.DESCRIPTION_STALE_INSTRUCTIONS' />
            </p>;
          break;
        case errorActions.reset.type:
          descriptionTitle = <FormattedMessage id='publishSteps.DESCRIPTION_RESET' />;
          break;
        case errorActions.resetToDraft.type:
          descriptionTitle = <FormattedMessage id='publishSteps.DESCRIPTION_RESET_TO_DRAFT' />;
          break;
        default:
          break;
      }
    }
    const actionDate = action.data && intl.formatDate(action.data?.timestamp);
    const actionTime = action.data && intl.formatTime(action.data?.timestamp);
    const subtitle = action.data?.timestamp &&
      <FormattedMessage
        id='publishSteps.DESCRIPTION_SUBTITLE'
        values={{ name: action.data.backupByName, date: actionDate, time: actionTime }}
      />;
    return (
      <StyledSpan>
        <DescriptionTitle>{descriptionTitle}</DescriptionTitle>
        <DescriptionSubTitle>{subtitle}</DescriptionSubTitle>
      </StyledSpan>
    );
  };

  const showNotification = (action) => {
    const actionTitle = action.typeName.toUpperCase();
    notification.warning({
      message: intl.formatMessage({ id: `publishSteps.${actionTitle}_TITLE` }),
      placement: 'top',
      description: <WarningNotification
        onConfirm={() => {
          handleButtonClick(action);
          notification.destroy('planningWarning');
        }}
        onClose={() => {
          notification.destroy('planningWarning');
        }}
        body={<>
          <Text>{intl.formatMessage({ id: `publishSteps.${actionTitle}_INFO` })}</Text>
          <Text marginTop={15}>
            {intl.formatMessage({ id: `publishSteps.${actionTitle}_INSTRUCTION` })}
          </Text>
        </>}
        isLoading={false}
        confirmButtonText={intl.formatMessage({ id: `publishSteps.${actionTitle}_ACTION` })}
        closeButtonText={intl.formatMessage({ id: 'common.CANCEL' })}
      />,
      key: 'planningWarning',
      className: 'extended-notification',
      duration: 0,
    });
  };

  const renderActionButton = (action) => {
    let text = <FormattedMessage id='publishSteps.BUTTON_NONE' />;

    if (action) {
      switch (action.type) {
        case errorActions.delete.type:
          text = <FormattedMessage id='publishSteps.BUTTON_DELETE' />;
          break;
        case errorActions.update.type:
          text = <FormattedMessage id='publishSteps.BUTTON_UPDATE' />;
          break;
        case errorActions.reset.type:
          text = <FormattedMessage id='publishSteps.BUTTON_RESET' />;
          break;
        case errorActions.resetToDraft.type:
          text = <FormattedMessage id='publishSteps.BUTTON_RESET_TO_DRAFT' />;
          break;
        default:
          break;
      }
    }

    return (
      // Update is secondary button style and in last step reset is secondary button style
      <StyledActionButton
        type={
          (action && action.type === errorActions.update.type) ||
            (action &&
              status === publishSteps.receiving.status &&
              action.type === errorActions.reset.type)
            ? 'secondary'
            : ''
        }
        disabled={
          isLoading || (action && action.type === errorActions.update.type && isStale) ||
          (action.type === errorActions.delete.type && isDeletingDraft)
        }
        onClick={
          hasPlanningItems ? () => showNotification(action) : () => handleButtonClick(action)
        }
        loading={isLoading || (action.type === errorActions.delete.type && isDeletingDraft)}
      >
        {text}
      </StyledActionButton>
    );
  };

  const renderGtsInfo = () =>
    <>
      <StyledMessage>
        {intl.formatMessage(
          {
            id: `publishSteps.${isGtsBlockedPlanning ?
              'GTS_BLOCKED_INFO_PLANNING' :
              'GTS_BLOCKED_INFO'}`,
          },
          {
            b: msg => <b>{msg}</b>,
          },
        )}
      </StyledMessage>
      <StyledUl>
        <StyledLi isGts={isGtsBlocked}>
          <FormattedMessage id='publishSteps.GTS_BLOCKED_INFO_LIST_ONE' />
        </StyledLi>
        <StyledLi isGts={isGtsBlocked}>
          <FormattedMessage id='publishSteps.GTS_BLOCKED_INFO_LIST_TWO' />
        </StyledLi>
      </StyledUl>
    </>;

  const renderValidationDescription = () =>
    <StyledUl>
      <StyledLi>
        <FormattedMessage id='publishSteps.VALIDATION_ERROR_ACTION' />
      </StyledLi>
      <StyledLi>
        <FormattedMessage id='publishSteps.VALIDATION_ERROR_UPDATE' />
      </StyledLi>
      {!isObjectNullOrEmpty(backupHistory) &&
        <StyledLi>
          <FormattedMessage id='publishSteps.VALIDATION_ERROR_REFRESH' />
          {backupHistory?.timestamp ? <DescriptionSubTitle>
            <FormattedMessage
              id='publishSteps.DESCRIPTION_SUBTITLE'
              values={{
                name: backupHistory.backupByName,
                date: intl.formatDate(backupHistory?.timestamp),
                time: intl.formatTime(backupHistory?.timestamp),
              }}
            />
          </DescriptionSubTitle> :
            null}
        </StyledLi>
      }
    </StyledUl>;

  const renderLinkedErrorDescription = () =>
    <StyledMessage>
      <FormattedMessage
        id='publishSteps.LINKED_ERROR_DESCRIPTION'
        values={{ type: otherEntityType }}
      />
    </StyledMessage>;
  const renderActions = () => {
    if (isGtsBlocked) {
      return renderGtsInfo();
    }
    if (actions && actions.length) {
      return actions?.map((action, index) =>
        <StyledRow type='flex' align='middle' key={index} >
          <ButtonCol>
            {action && renderActionButton(action)}
          </ButtonCol>
          <Col>
            {action && renderActionDescription(action)}
          </Col>
        </StyledRow>,
      );
    } else if (actions && !actions.length && isValidationError) {
      return (
        <StyledRow type='flex'>
          <Col xs={24} sm={24}>
            {renderValidationDescription()}
          </Col>
        </StyledRow>
      );
    } else if (actions && !actions.length && isFromOtherEntity) {
      return (
        <StyledRow type='flex'>
          <Col xs={24} sm={24}>
            {renderLinkedErrorDescription()}
          </Col>
        </StyledRow>
      );
    }
    return (
      <StyledMessage>
        <FormattedMessage id='publishSteps.ACTION_NONE' />
      </StyledMessage>
    );
  };

  const renderTimeOutMessage = () => {
    if (isTimedOut) {
      return (
        <StyledMessage>
          <FormattedMessage id='publishSteps.TIME_OUT' values={{ timeoutInMinutes }} />
          <br />
          <FormattedMessage id='publishSteps.TIME_OUT_INSTRUCTIONS' />
        </StyledMessage>
      );
    }
    return null;
  };

  const renderGTSMessage = () => {
    if (isGtsBlocked) {
      return (
        <StyledMessage>
          <FormattedMessage
            id={`publishSteps.${isGtsBlockedPlanning ?
              'GTS_BLOCKED_ERROR_PLANNING' :
              'GTS_BLOCKED_ERROR'}`
            }
          />
        </StyledMessage>
      );
    }
    return null;
  };

  const renderLinkedErrorMessage = () => {
    if (isFromOtherEntity) {
      return (
        <StyledMessage>
          <FormattedMessage id='publishSteps.LINKED_ERROR' values={{ type: otherEntityType }} />
        </StyledMessage>
      );
    }
    return null;
  };

  const renderInfoActions = () =>
    <InfoAction>
      <StyledMessage>
        <FormattedMessage id='publishSteps.INFO_ACTIONS' />
      </StyledMessage>
    </InfoAction>;

  const renderWarningActions = () =>
    <InfoAction>
      <StyledMessage>
        <FormattedMessage id='publishSteps.SAP_WARNING_INSTRUCTION' />
      </StyledMessage>
    </InfoAction>;

  const getInfoDescription = () => {
    if (isExternalUpdate) {
      return <FormattedMessage id='publishSteps.EXTERNAL_UPDATE_INFO_DESCRIPTION' />;
    } else if (isExternalCreate) {
      return <FormattedMessage id='publishSteps.EXTERNAL_CREATE_INFO_DESCRIPTION' />;
    } else if (isFromXML) {
      return <FormattedMessage id='publishSteps.XML_INFO_DESCRIPTION' />;
    }
    return <FormattedMessage id='publishSteps.INFO_DESCRIPTION' />;
  };

  const getInfoTitle = () => {
    if (isExternalUpdate) {
      return <FormattedMessage id='publishSteps.EXTERNAL_UPDATE_INFO_TITLE' />;
    } else if (isExternalCreate) {
      return <FormattedMessage id='publishSteps.EXTERNAL_CREATE_INFO_TITLE' />;
    } else if (isFromXML) {
      return <FormattedMessage id='publishSteps.XML_INFO_TITLE' />;
    }
    return <FormattedMessage id='publishSteps.INFO_TITLE' />;
  };

  const renderInfo = () =>
    <>
      <StyledMessage>
        {getInfoDescription()}{':'}
      </StyledMessage>
      <StyledUl>
        {info?.map((i, index) =>
          <StyledLi key={index}>{i}</StyledLi>,
        )}
      </StyledUl>
    </>;

  const renderWarning = () =>
    <>
      <StyledMessage>
        <FormattedMessage id='publishSteps.SAP_WARNING' />{':'}
      </StyledMessage>
      <StyledUl>
        {warnings?.map((i, index) =>
          <StyledLi key={index}>{i}</StyledLi>,
        )}
      </StyledUl>
    </>;

  const renderErrors = () =>
    <>
      {renderTimeOutMessage()}
      {renderLinkedErrorMessage()}
      {renderGTSMessage()}
      <StyledUl>
        {errors?.map((error, index) =>
          <StyledLi isGts={isGtsBlocked} key={index}>{error}</StyledLi>,
        )}
      </StyledUl>
    </>;

  const renderArrow = validationStatus => <Arrow
    onClick={() => {
      if (resizeCb) {
        resizeCb(isCollapsed);
      }
      setCollapsed(!isCollapsed);
    }}
    validationStatus={validationStatus}
  >
    <FormattedMessage id={`publishSteps.SHOW_${isCollapsed ? 'MORE' : 'LESS'}`} />{' '}{arrow}
  </Arrow>;

  const showMoreRender = (validationStatus) => {
    if (isStandAlone) {
      return renderArrow(validationStatus);
    }
    return renderShowMoreToggle ? <ShowMore>{renderShowMoreToggle()}</ShowMore> : null;
  };

  const renderVerticalVersion = () => <>
    {((errors && errors.length > 0) || isTimedOut) &&
      <StyledDiv
        isValidationError={isValidationError}
        hasErrors={errors?.length > 0}
        hasWarnings={warnings?.length > 0}
        isStandAlone={isStandAlone}
      >
        {isCollapsed && <ErrorTitleCollapsed>
          <FormattedMessage id='publishSteps.THERE_ARE_ERRORS' />
          {isStandAlone && renderArrow()}
        </ErrorTitleCollapsed>}
        {content(
          <>
            <Row type='flex'>
              <StyledCol span={24} paddingleft='40px'>
                <ErrorTitle>
                  <FormattedMessage id='publishSteps.ERROR_TITLE_WRONG' />
                  {isStandAlone && renderArrow()}
                </ErrorTitle>
                {renderErrors()}
              </StyledCol>
              <StyledCol span={24} paddingleft='40px' paddingright='40px'>
                <ErrorTitle>
                  <FormattedMessage id='publishSteps.ERROR_TITLE_OPTIONS' />
                </ErrorTitle>
                {renderActions()}
              </StyledCol>
            </Row>
            {showMoreRender()}
          </>,
        )}
      </StyledDiv>
    }
  </>;

  const renderExternal = () =>
    <StyledDiv
      isValidationError={isValidationError}
      isStandAlone={isStandAlone}
      hasErrors={errors?.length > 0}
      hasInfo={errors?.length === 0 && info?.length > 0}
      hasWarnings={warnings?.length > 0}
    >
      <Row type='flex'>
        <StyledCol
          span={24}
          paddingleft='150px'
          paddingright='40px'
          bordered='true'
        >
          <SmallExternalInfoMessage>
            <StyledInfoIcon className='fa-solid fa-square-info' />
            {isExternalUpdate ?
              <FormattedMessage
                id='publishSteps.EXTERNAL_UPDATE_INFO_DESCRIPTION_WITHOUT_INFO'
              />
              :
              <FormattedMessage
                id='publishSteps.EXTERNAL_CREATE_INFO_DESCRIPTION_WITHOUT_INFO'
              />}
          </SmallExternalInfoMessage>
        </StyledCol>
      </Row>
    </StyledDiv>;

  const renderErrorBlock = () => errors?.length > 0 || isTimedOut || actionOnly ?
    <Row type='flex'>
      <StyledCol
        span={12}
        paddingleft='150px'
        paddingright='40px'
        bordered='true'
      >
        <ErrorTitle>
          <FormattedMessage id='publishSteps.ERROR_TITLE_WRONG' />
          {isStandAlone && renderArrow()}
        </ErrorTitle>
        {actionOnly ? <FormattedMessage id='publishSteps.UNEXPECTED_LOCK' /> : renderErrors()}
      </StyledCol>
      <StyledCol span={12} paddingleft='40px' paddingright='40px'>
        <ErrorTitle>
          <FormattedMessage id='publishSteps.ERROR_TITLE_OPTIONS' />
        </ErrorTitle>
        {renderActions()}
      </StyledCol>
    </Row> :
    null;

  const renderInfoBlock = () => info?.length > 0 ?
    <Row type='flex'>
      <StyledCol
        span={12}
        paddingleft='150px'
        paddingright='40px'
        bordered='true'
      >
        <InfoTitle>
          {getInfoTitle()}
          {!errors?.length && isStandAlone && renderArrow(true)}
        </InfoTitle>
        {renderInfo()}
      </StyledCol>
      <StyledCol span={12} paddingleft='40px' paddingright='40px'>
        <InfoTitle>
          <FormattedMessage id='publishSteps.ERROR_TITLE_OPTIONS' />
        </InfoTitle>
        {renderInfoActions()}
      </StyledCol>
    </Row> :
    null;

  const renderWarningBlock = () => warnings?.length > 0 ?
    <Row type='flex'>
      <StyledCol
        span={12}
        paddingleft='150px'
        paddingright='40px'
        bordered='true'
      >
        <WarningTitle>
          <FormattedMessage id='publishSteps.WHAT_WENT_WRONG' />
          {!warnings?.length && isStandAlone && renderArrow(true)}
        </WarningTitle>
        {renderWarning()}
      </StyledCol>
      <StyledCol span={12} paddingleft='40px' paddingright='40px'>
        <WarningTitle>
          <FormattedMessage id='publishSteps.ERROR_TITLE_OPTIONS' />
        </WarningTitle>
        {renderWarningActions()}
      </StyledCol>
    </Row> :
    null;

  const getStatus = () => {
    if (!errors?.length && !isTimedOut && !actionOnly && !warnings?.length) {
      return 1;
    } else if (!errors?.length && !isTimedOut && !actionOnly && warnings?.length) {
      return 2;
    }
    return 3;
  };

  const getCollapsedDescription = (validationStatus) => {
    if (validationStatus === 1) {
      return 'publishSteps.THERE_IS_INFO';
    } else if (validationStatus === 2) {
      return 'publishSteps.THERE_ARE_WARNINGS';
    }
    return 'publishSteps.THERE_ARE_ERRORS';
  };

  const translateY = -106;
  const updatedTranslateY = hasAnnouncementBanner ?
    `${translateY + announcementBannerHeight}px` :
    `${translateY}px`;
  const originalTranslateY = hasAnnouncementBanner && window.pageYOffset > 10 ?
    `${announcementBannerHeight}px` :
    '0px';

  const validationStatus = getStatus();

  return (
    <>
      {verticalVersion ?
        renderVerticalVersion() :
        <Spring
          to={{
            transform: menuVisible ?
              `translateY(${originalTranslateY})` :
              `translateY(${updatedTranslateY})`,
            boxShadow: menuVisible ? '0px 0px 0px 0px gray' : '0px 5px 20px -5px gray',
          }}
          config={{ duration: 400 }}
        >
          {props =>
            <StyledContainer style={isValidationError ? props : {}} extraStyles={extraStyles}>
              {(isExternalCreate || isExternalUpdate) && renderExternal()}
              {((errors?.length > 0 || info?.length > 0 || warnings?.length) ||
                isTimedOut ||
                actionOnly
              ) &&
                <StyledDiv
                  isValidationError={isValidationError}
                  hasErrors={errors?.length > 0 || actionOnly}
                  isStandAlone={isStandAlone}
                  hasInfo={errors?.length === 0 && info?.length > 0}
                  hasWarnings={warnings?.length > 0}
                >
                  {isCollapsed && <ErrorTitleCollapsed
                    validationStatus={validationStatus}
                  >
                    <FormattedMessage id={getCollapsedDescription(validationStatus)} />
                    {isStandAlone && renderArrow(validationStatus)}
                  </ErrorTitleCollapsed>
                  }
                  {content(
                    <>
                      {renderErrorBlock()}
                      {renderInfoBlock()}
                      {renderWarningBlock()}
                      {showMoreRender(validationStatus)}
                    </>,
                  )}
                </StyledDiv>
              }
            </StyledContainer>
          }
        </Spring>
      }
    </>
  );
};

ErrorSummary.propTypes = {
  errors: PropTypes.array,
  info: PropTypes.array,
  warnings: PropTypes.array,
  actions: PropTypes.array,
  isTimedOut: PropTypes.bool,
  isStale: PropTypes.bool,
  isLoading: PropTypes.bool,
  backupHistory: PropTypes.object,
  unlockLockRequest: PropTypes.func,
  discardLockRequest: PropTypes.func,
  deleteDraftRequest: PropTypes.func,
  intl: PropTypes.object,
  status: PropTypes.number,
  timeoutInMinutes: PropTypes.number,
  isValidationError: PropTypes.bool,
  menuVisible: PropTypes.bool,
  onDataReloaded: PropTypes.func,
  verticalVersion: PropTypes.bool,
  isPlanningBoard: PropTypes.bool,
  hasPlanningItems: PropTypes.bool,
  isFromOtherEntity: PropTypes.bool,
  otherEntityType: PropTypes.string,
  renderShowMoreToggle: PropTypes.func,
  isExternalCreate: PropTypes.bool,
  isExternalUpdate: PropTypes.bool,
  extraStyles: PropTypes.object,
  isGtsBlocked: PropTypes.bool,
  isGtsBlockedPlanning: PropTypes.bool,
  isDeletingDraft: PropTypes.bool,
  isStandAlone: PropTypes.bool,
  resizeCb: PropTypes.func,
  actionOnly: PropTypes.bool,
  isFromXML: PropTypes.bool,
};

export default injectIntl(ErrorSummary);
